import {
  IFlattenAndCategorizeResult,
  ISelectedTags,
  ISelectionState,
  ITaxonomyApiResponseContent,
  ITaxonomyCategorizedData,
  ITaxonomyContent,
  ITaxonomyTags,
  ITopicsCheckbox,
} from "../TaxonomyTags.types";

const processItem = (
  item: ITaxonomyApiResponseContent,
  flattenedData: ITaxonomyContent[],
  categorizedData: ITaxonomyCategorizedData,
  parentId: string | null,
  level: number,
  hierarchy: string,
) => {
  const currentHierarchy = hierarchy
    ? `${hierarchy} > ${item.name}`
    : item.name;
  const content: ITaxonomyContent = {
    id: item.content_id,
    name: item.name,
    level,
    parentId,
    label: item.name,
    value: item.slug,
    hierarchy: currentHierarchy,
    children: item.children
      ? item.children.map((child) => child.content_id)
      : [],
  };

  flattenedData.push(content);

  if (!categorizedData[`level${level}`]) {
    categorizedData[`level${level}`] = [];
  }
  categorizedData[`level${level}`]?.push(content);

  if (item.children && item.children.length > 0) {
    item.children.forEach((child) =>
      processItem(
        child,
        flattenedData,
        categorizedData,
        item.content_id,
        level + 1,
        currentHierarchy,
      ),
    );
  }
};

export const flattenAndCategorizeData = (
  data: ITaxonomyApiResponseContent[],
  parentId: string | null = null,
  level = 0,
  parentHierarchy = "",
): IFlattenAndCategorizeResult => {
  const flattenedData: ITaxonomyContent[] = [];
  const categorizedData: ITaxonomyCategorizedData = {};

  data.forEach((item) =>
    processItem(
      item,
      flattenedData,
      categorizedData,
      parentId,
      level,
      parentHierarchy,
    ),
  );

  if (categorizedData["level1"]) {
    categorizedData["level1"].sort((a, b) =>
      (a.parentId ?? "").localeCompare(b.parentId ?? ""),
    );
  }

  return { flattenedData, categorizedData };
};

const findItemInCategorizedData = (
  id: string,
  categorizedData: ITaxonomyCategorizedData,
) => {
  for (const level in categorizedData) {
    if (categorizedData[level]) {
      for (const item of categorizedData[level] ?? []) {
        if (item.id === id) {
          return item;
        }
      }
    }
  }
  return null;
};

export const buildSelectedTags = (
  selectionState: ISelectionState,
  categorizedData: ITaxonomyCategorizedData,
) => {
  const selectedTags: ISelectedTags = {};

  for (const id in selectionState) {
    const state = selectionState[id];

    if (
      typeof state !== "boolean" &&
      (state?.selected || state?.isSemiSelected)
    ) {
      const item = findItemInCategorizedData(id, categorizedData);
      if (item) {
        selectedTags[item.value] = {
          level: item.level,
          parent: item.parentId
            ? findItemInCategorizedData(item.parentId, categorizedData)?.value
            : null,
        };
      }
    }
  }

  return selectedTags;
};

const buildTagTree = (
  selectedTags: ISelectedTags,
): { [key: string]: string[] } => {
  const tagTree: { [key: string]: string[] } = {};
  Object.keys(selectedTags).forEach((tag) => {
    const selectedTag = selectedTags[tag];
    if (selectedTag) {
      const { parent } = selectedTag;
      if (parent && parent !== null) {
        if (!tagTree[parent] || tagTree[parent] === null) {
          tagTree[parent] = [];
        }
        tagTree[parent]?.push(tag);
      }
    }
  });
  return tagTree;
};

const buildTree = (
  currentTag: string,
  tagTree: { [key: string]: string[] },
  currentTree: ITaxonomyTags,
) => {
  if (!tagTree[currentTag]) return;
  currentTree[currentTag] = currentTree[currentTag] || [];
  tagTree[currentTag]?.forEach((childTag) => {
    const childTree: ITaxonomyTags = {};
    buildTree(childTag, tagTree, childTree);
    if (Object.keys(childTree).length > 0) {
      (currentTree[currentTag] as Array<string | ITaxonomyTags>).push(
        childTree,
      );
    } else {
      (currentTree[currentTag] as Array<string | ITaxonomyTags>).push(childTag);
    }
  });
};

export const buildTaxonomyTags = (
  selectedTags: ISelectedTags,
): ITaxonomyTags => {
  const taxonomyTags: ITaxonomyTags = {};

  Object.keys(selectedTags)?.forEach((tag) => {
    const selectedTag = selectedTags[tag];
    if (selectedTag && selectedTag.parent === null) {
      taxonomyTags[tag] = "";
    }
  });

  const tagTree = buildTagTree(selectedTags);

  Object.keys(taxonomyTags).forEach((tag) =>
    buildTree(tag, tagTree, taxonomyTags),
  );

  return taxonomyTags;
};

export const getTop5ELA = (
  categorizedData: ITaxonomyCategorizedData,
): ITopicsCheckbox[] => {
  const elaId = categorizedData["level0"]?.find(
    (data) => data.value === "ela",
  )?.id;

  const elaLevel1 = categorizedData["level1"]
    ?.filter((data) => data.parentId === elaId)
    .map((data) => ({
      label: data.label,
      value: data.id,
      slug: data.value,
    }));

  return elaLevel1?.slice(0, 5) || [];
};

export const collectTaxonomySlugs = (obj: ITaxonomyTags): string[] => {
  const result = new Set<string>();

  const traverse = (
    node: string | ITaxonomyTags | Array<string | ITaxonomyTags> | undefined,
  ) => {
    if (typeof node === "string") {
      result.add(node);
    } else if (Array.isArray(node)) {
      node.forEach((item) => traverse(item));
    } else if (typeof node === "object" && node !== null) {
      Object.keys(node).forEach((key) => {
        result.add(key);
        traverse(node[key]);
      });
    }
  };

  traverse(obj);

  return Array.from(result).filter(
    (value) => !["ela", "social-studies", "science"].includes(value),
  );
};

export const removeKeyFromTaxonomyTags = (
  obj: ITaxonomyTags,
  keyToRemove: string,
): ITaxonomyTags => {
  const newObj: ITaxonomyTags = {};

  for (const key in obj) {
    const value = obj[key];

    if (key === keyToRemove) {
      continue;
    }

    if (Array.isArray(value)) {
      const newArray = removeValueFromArray(value, keyToRemove);
      if (newArray.length > 0) {
        newObj[key] = newArray;
      }
    } else if (typeof value === "object" && value !== null) {
      const newObject = removeKeyFromTaxonomyTags(value, keyToRemove);
      if (Object.keys(newObject).length > 0) {
        newObj[key] = [newObject];
      }
    } else if (typeof value === "string") {
      newObj[key] = value;
    }
  }

  return newObj;
};

export const removeValueFromArray = (
  arr: Array<string | ITaxonomyTags>,
  valueToRemove: string,
): Array<string | ITaxonomyTags> => {
  const newArray: Array<string | ITaxonomyTags> = [];

  for (const item of arr) {
    if (typeof item === "string" && item === valueToRemove) {
      continue;
    } else if (typeof item === "object" && item !== null) {
      const newObject = removeKeyFromTaxonomyTags(item, valueToRemove);
      if (Object.keys(newObject).length > 0) {
        newArray.push(newObject);
      }
    } else {
      newArray.push(item);
    }
  }

  return newArray;
};

export const getUniqueElements = (arr1: string[], arr2: string[]): string[] => {
  const uniqueToArray1 = arr1.filter((item) => !arr2.includes(item));
  const uniqueToArray2 = arr2.filter((item) => !arr1.includes(item));

  return uniqueToArray1.concat(uniqueToArray2);
};

export const getIdsFromDifferenceInQuery = (
  differenceInQuery: string[],
  categorizedData: ITaxonomyCategorizedData,
): string[] => {
  const idArray: string[] = [];

  // Iterate over each value in differenceInQuery
  differenceInQuery.forEach((queryValue) => {
    // Iterate over each key in categorizedData
    for (const key in categorizedData) {
      // Find the matching ITaxonomyContent object
      const matchingItem = categorizedData[key]?.find(
        (item) => item.value === queryValue,
      );

      if (matchingItem) {
        idArray.push(matchingItem.id);
        break; // Stop searching once a match is found for the current queryValue
      }
    }
  });

  return idArray;
};
